import { Box, alpha, lighten, useTheme } from '@mui/material';
import { FC, ReactNode, useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import BreadcrumbNav from 'src/components/Navigation/BreadcrumbNav';
import WhatsNewDialog from 'src/components/WhatsNewDialog/WhatsNewDialog';
import appConfig from 'src/config/appConfig';
import { DashboardProvider } from 'src/context/dashboardContext';
import { SidebarContext } from 'src/context/SidebarContext';
import useIsMobile from 'src/hooks/useIsMobile';
import CollapsibleSidebar from 'src/layouts/CollapsibleSidebar';
import NavBar from 'src/layouts/NavBar';
import { isScopedDashboardPath } from 'src/router/pathUtils';
import pages from 'src/router/routes';

import TopBar from './TopBar';

interface SidebarLayoutProps {
  children?: ReactNode;
}

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const location = useLocation();
  const { isCollapsed } = useContext(SidebarContext);
  const isMobile = useIsMobile();

  const sidebarWidth = isMobile
    ? '0px'
    : isCollapsed
      ? '70px'
      : theme.sidebar.width;

  const isDashboardPage =
    location.pathname === pages.home.path ||
    location.pathname === pages.dashboard.path ||
    location.pathname === '/' ||
    isScopedDashboardPath(location.pathname);

  //@ts-ignore
  document.body.style = `background: ${theme.general.reactFrameworkColor};`;

  return (
    <>
      <WhatsNewDialog />
      <Box
        sx={{
          flex: 1,
          height: '100%',

          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                    lighten(theme.colors.primary.main, 0.7),
                    0.15
                  )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                    theme.colors.alpha.black[100],
                    0.1
                  )}, 0px 5px 12px -4px ${alpha(
                    theme.colors.alpha.black[100],
                    0.05
                  )}`,
          },
        }}
      >
        <NavBar />
        <CollapsibleSidebar />
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'block',
            flex: 1,
            pt: `${theme.header.height}`,
            transition: 'margin-left 0.3s ease-in-out',
            [theme.breakpoints.up(appConfig.breakMobileView)]: {
              ml: sidebarWidth,
            },
          }}
        >
          {isDashboardPage ? (
            <DashboardProvider>
              <TopBar />
              <Outlet />
            </DashboardProvider>
          ) : (
            <>
              <BreadcrumbNav showHome={false} />
              <Outlet />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
